import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, CalendarClock, CheckCircle2, AlertTriangle } from "lucide-react";
import { listMyAssignments, saveMySubmission } from "@/lib/api/student-portal.functions";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

export const Route = createFileRoute("/_authenticated/portal/student/assignments")({
  component: AssignmentsPage,
});

function AssignmentsPage() {
  const list = useServerFn(listMyAssignments);
  const save = useServerFn(saveMySubmission);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["student-assignments"], queryFn: () => list() });
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function submit(id: string, asDraft: boolean, current: string) {
    setBusy(id);
    try {
      const res = await save({ data: { assignment_id: id, body: current, submit: !asDraft } });
      toast.success(asDraft ? "Draft saved" : res.late ? "Submitted (marked late)" : "Submitted");
      qc.invalidateQueries({ queryKey: ["student-assignments"] });
      qc.invalidateQueries({ queryKey: ["student-dashboard"] });
    } catch (err: any) {
      toast.error("Could not save", { description: err?.message });
    } finally {
      setBusy(null);
    }
  }

  if (q.isLoading) return <Loader2 className="h-5 w-5 animate-spin text-navy" />;
  const assignments = q.data?.assignments ?? [];

  return (
    <div className="space-y-4">
      {assignments.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No assignments have been published for your qualification yet.
        </p>
      )}
      {assignments.map((a: any) => {
        const sub = a.submission;
        const value = drafts[a.id] ?? sub?.body ?? "";
        const due = a.due_at ? new Date(a.due_at) : null;
        const overdue = due && !sub?.submitted_at && due < new Date();
        return (
          <article key={a.id} className="rounded-lg border border-border bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 className="font-display text-lg text-navy">{a.title}</h3>
                {a.course_label && (
                  <p className="text-xs uppercase tracking-widest text-muted-foreground">
                    {a.course_label}
                  </p>
                )}
              </div>
              <span
                className={
                  overdue
                    ? "inline-flex items-center gap-1 rounded-full bg-destructive/10 px-3 py-1 text-xs text-destructive"
                    : "inline-flex items-center gap-1 rounded-full bg-navy/5 px-3 py-1 text-xs text-navy"
                }
              >
                {overdue ? <AlertTriangle className="h-3 w-3" /> : <CalendarClock className="h-3 w-3" />}
                {due ? due.toLocaleString() : "No deadline"}
              </span>
            </div>

            {a.description && (
              <p className="mt-2 whitespace-pre-wrap text-sm text-foreground/85">{a.description}</p>
            )}

            {sub?.status === "submitted" ? (
              <div className="mt-3 rounded-md bg-muted/50 p-3 text-sm">
                <p className="flex items-center gap-2 text-navy">
                  <CheckCircle2 className="h-4 w-4" />
                  Submitted {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : ""}
                  {sub.is_late ? " (late)" : ""}
                </p>
                {sub.mark !== null && sub.mark !== undefined && (
                  <p className="mt-1 text-navy">
                    Mark: <strong>{sub.mark}</strong> / {a.max_marks ?? 100}
                  </p>
                )}
                {sub.feedback && (
                  <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{sub.feedback}</p>
                )}
              </div>
            ) : (
              <div className="mt-3 space-y-2">
                <label className="text-xs uppercase tracking-widest text-muted-foreground" htmlFor={`sub-${a.id}`}>
                  Your answer
                </label>
                <Textarea
                  id={`sub-${a.id}`}
                  rows={6}
                  value={value}
                  onChange={(e) => setDrafts({ ...drafts, [a.id]: e.target.value })}
                  placeholder="Write or paste your submission…"
                />
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={busy === a.id}
                    onClick={() => submit(a.id, true, value)}
                  >
                    Save draft
                  </Button>
                  <Button
                    type="button"
                    disabled={busy === a.id || !value.trim()}
                    onClick={() => submit(a.id, false, value)}
                    className="bg-navy hover:bg-navy/90"
                  >
                    {busy === a.id ? "Working…" : "Submit"}
                  </Button>
                </div>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
